import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Instagram, Users, CheckCircle, Lock, ExternalLink, Eye, EyeOff, Music, Image as ImageIcon } from 'lucide-react';
import { stalkerAPI, getStaticFileUrl, getProxyUrl } from '../../services/api';
import { useSearch } from '../../contexts/SearchContext';
import { useDarkMode } from '../../contexts/DarkModeContext';
import { PLATFORM_MAP } from '../../utils/enums';
import loggingService from '../../services/loggingService';

type PastLogPoint = {
	created_at: string;
	value: number | null;
	rawOld: any;
	rawNew: any;
};

const getFieldFromChangeType = (changeType: string) => {
	const t = (changeType || '').trim();
	if (!t.endsWith('_changed')) return null;
	return t.slice(0, -'_changed'.length);
};

const pickValue = (val: any, field: string | null) => {
	if (field && val && typeof val === 'object' && field in val) return val[field];
	return val;
};

const toNumberOrNull = (v: any): number | null => {
	if (v === null || v === undefined) return null;
	const n = typeof v === 'number' ? v : Number(v);
	return Number.isFinite(n) ? n : null;
};

const buildSeries = (logs: any[], changeType: string): PastLogPoint[] => {
	const field = getFieldFromChangeType(changeType);
	return logs
		.filter((l) => l.sl_changeType === changeType)
		.map((l) => {
			const oldRaw = pickValue(l.sl_oldValue, field);
			const newRaw = pickValue(l.sl_newValue, field);
			const val = toNumberOrNull(newRaw ?? oldRaw);
			return {
				created_at: l.created_at,
				value: val,
				rawOld: oldRaw,
				rawNew: newRaw,
			};
		})
		.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
};

const MiniLineChart: React.FC<{
	points: PastLogPoint[];
	color?: string;
	defaultLabelValue?: number | null;
	defaultLabelDate?: string | null;
}> = ({ points, color = '#E50914', defaultLabelValue, defaultLabelDate }) => {
	const realPoints = points.filter((p) => p.value !== null);

	let processedPoints = points;
	let values = processedPoints.map((p) => p.value).filter((v): v is number => v !== null);

	if (!processedPoints.length || !values.length) {
		const nowIso = new Date().toISOString();
		const fallbackVal = defaultLabelValue ?? 0;
		const baseDate = defaultLabelDate || nowIso;
		processedPoints = [
			{ created_at: baseDate, value: fallbackVal, rawOld: null, rawNew: null },
			{ created_at: baseDate, value: fallbackVal, rawOld: null, rawNew: null },
		];
		values = [fallbackVal];
	}

	if (realPoints.length === 1 && processedPoints.length === 1) {
		const single = processedPoints[0];
		processedPoints = [
			single,
			{ ...single },
		];
		values = [single.value ?? 0, single.value ?? 0];
	}

	let min = Math.min(...values);
	let max = Math.max(...values);
	if (min === max) {
		const v = min;
		min = v - 1;
		max = v + 1;
	}
	const span = max - min || 1;
	const width = 100;
	const height = 30;
	const paddingLeft = 2;
	const paddingRight = 2;
	const paddingTop = 2;
	const paddingBottom = 2;
	const innerWidth = width - paddingLeft - paddingRight;
	const innerHeight = height - paddingTop - paddingBottom;

	const pointsWithCoords = processedPoints.map((p, idx) => {
		const value = p.value ?? 0;
		const x = paddingLeft + (idx / Math.max(processedPoints.length - 1, 1)) * innerWidth;
		const normY = (value - min) / span;
		const y = paddingTop + (1 - normY) * innerHeight;
		return { ...p, x, y, value };
	});

	const d = pointsWithCoords.map((p) => `${p.x},${p.y}`).join(' ');

	return (
		<svg viewBox={`0 0 ${width} ${height}`} className="w-full h-full max-h-8" preserveAspectRatio="none">
			<polyline
				fill="none"
				stroke={color}
				strokeWidth="1.5"
				points={d}
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
};

const LoadingMiniLineChart: React.FC<{ isDark?: boolean }> = ({ isDark }) => (
	<div className="relative w-full h-full max-h-8 flex items-center justify-center">
		<div className={`w-full h-[1px] ${isDark ? 'bg-[#333]' : 'bg-neutral-200'} absolute`}></div>
		<div className={`w-3 h-3 border border-t-transparent ${isDark ? 'border-emerald-500/50' : 'border-emerald-500/50'} rounded-none animate-spin z-10 box-border`}></div>
	</div>
);

const Socials: React.FC = () => {
	const { globalSearchTerm } = useSearch();
	const { isDark } = useDarkMode();
	const navigate = useNavigate();
	const [socials, setSocials] = useState<any[]>([]);
	const [histories, setHistories] = useState<Record<string, any>>({});
	const [loading, setLoading] = useState(true);
	const [showHidden, setShowHidden] = useState(false);
	const [sortBy, setSortBy] = useState<string>(() => localStorage.getItem('socialsSort') || 'create_time_desc');
	const hasLoggedPageView = useRef(false);

	useEffect(() => {
		loadData();
		if (!hasLoggedPageView.current) {
			loggingService.logPageView('/stalker/socials', 'Stalker', 'Socials');
			hasLoggedPageView.current = true;
		}
	}, []);

	useEffect(() => {
		localStorage.setItem('socialsSort', sortBy);
	}, [sortBy]);

	const loadData = async () => {
		try {
			setLoading(true);
			const response = await stalkerAPI.socials.getList();
			const data = response.data.data;
			setSocials(data);
			setLoading(false);
			
			// Initial fetch for history records
			const historyMap: Record<string, any> = {};
			
			// We only want to fetch history for those that have stats (Instagram, TikTok)
			const accountsToFetch = data.filter((s: any) => s.platform === 'instagram' || s.platform === 'tiktok');
			
			// Define 7 days ago
			const startDate = new Date();
			startDate.setDate(startDate.getDate() - 7);
			
			// Fetch in parallel for performance
			await Promise.all(accountsToFetch.map(async (account: any) => {
				try {
					const id = account.id || account.iu_id || account.tu_id;
					const key = `${account.platform}-${id}`;
					
					const [followerResp, followingResp, mediaResp] = await Promise.all([
						stalkerAPI.logs.getPast({ accountId: Number(id), platform: account.platform, startDate: startDate.toISOString(), changeType: 'followerCount_changed', order: 'ASC' }),
						stalkerAPI.logs.getPast({ accountId: Number(id), platform: account.platform, startDate: startDate.toISOString(), changeType: 'followingCount_changed', order: 'ASC' }),
						stalkerAPI.logs.getPast({ accountId: Number(id), platform: account.platform, startDate: startDate.toISOString(), changeType: 'mediaCount_changed', order: 'ASC' })
					]);
					
					historyMap[key] = {
						followers: buildSeries(followerResp.data.data || [], 'followerCount_changed'),
						following: buildSeries(followingResp.data.data || [], 'followingCount_changed'),
						media: buildSeries(mediaResp.data.data || [], 'mediaCount_changed'),
					};
				} catch (err) {
					console.error(`Failed to load history for ${account.platform}-${account.id}`, err);
				}
			}));
			
			setHistories(historyMap);
		} catch (error) {
			console.error('Error loading socials:', error);
			setLoading(false);
		}
	};

	const filteredSocials = socials.filter(social => {
		const matchesSearch = globalSearchTerm === '' ||
			social.iu_username?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
			social.vu_username?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
			social.tu_username?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
			social.xu_username?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
			social.su_username?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
			social.iu_name?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
			social.vu_name?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
			social.tu_name?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
			social.xu_name?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
			social.su_name?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
			social.person_name?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
			social.person_surname?.toLowerCase().includes(globalSearchTerm.toLowerCase());
		const matchesHideFilter = showHidden || social.person_hideHome !== '1';
		return matchesSearch && matchesHideFilter;
	});

	const getUsername = (social: any) => {
		if (social.platform === 'instagram') return social.iu_username;
		if (social.platform === 'vsco') return social.vu_username;
		if (social.platform === 'tiktok') return social.tu_username;
		if (social.platform === 'x') return social.xu_username;
		if (social.platform === 'snapchat') return social.su_username;
		if (social.platform === 'spotify') return social.su_username;
		return '';
	};

	const getName = (social: any) => {
		if (social.platform === 'instagram') return social.iu_name;
		if (social.platform === 'vsco') return social.vu_name;
		if (social.platform === 'tiktok') return social.tu_name;
		if (social.platform === 'x') return social.xu_name;
		if (social.platform === 'snapchat') return social.su_name;
		if (social.platform === 'spotify') return social.su_name;
		return '';
	};

	const getProfilePicUrl = (social: any) => {
		if (social.platform === 'instagram') return social.iu_profilePicUrl;
		if (social.platform === 'tiktok') return social.tu_avatarUrl;
		if (social.platform === 'vsco') return social.vu_avatarUrl;
		return null;
	};

	const getFollowerCount = (social: any) => {
		if (social.platform === 'instagram') return social.iu_followerCount;
		if (social.platform === 'tiktok') return social.tu_followerCount;
		return null;
	};

	const getFollowingCount = (social: any) => {
		if (social.platform === 'instagram') return social.iu_followingCount;
		if (social.platform === 'tiktok') return social.tu_followingCount;
		return null;
	};

	const getMediaCount = (social: any) => {
		if (social.platform === 'instagram') return social.iu_mediaCount;
		if (social.platform === 'tiktok') return social.tu_mediaCount;
		return null;
	};

	const getIsVerified = (social: any) => {
		if (social.platform === 'instagram') return social.iu_isVerified === '1';
		if (social.platform === 'tiktok') return social.tu_verified === '1' || social.tu_verified === true;
		return false;
	};

	const getIsPrivate = (social: any) => {
		if (social.platform === 'instagram') return social.iu_isPrivate === '1';
		if (social.platform === 'tiktok') return social.tu_user_isPrivate === '1';
		return false;
	};

	const getSocialUrl = (social: any) => {
		const username = getUsername(social);
		if (!username) return null;
		if (social.platform === 'instagram') return `https://instagram.com/${username}`;
		if (social.platform === 'vsco') return `https://vsco.co/${username}`;
		if (social.platform === 'tiktok') return `https://tiktok.com/@${username}`;
		if (social.platform === 'x') return `https://x.com/${username}`;
		if (social.platform === 'snapchat') return `https://snapchat.com/add/${username}`;
		return null;
	};

	const sortedSocials = [...filteredSocials].sort((a, b) => {
		if (sortBy === 'create_time_desc') {
			return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
		}
		if (sortBy === 'create_time_asc') {
			return new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime();
		}
		if (sortBy === 'username_asc') {
			const aU = getUsername(a) || '';
			const bU = getUsername(b) || '';
			return aU.localeCompare(bU);
		}
		if (sortBy === 'username_desc') {
			const aU = getUsername(a) || '';
			const bU = getUsername(b) || '';
			return bU.localeCompare(aU);
		}
		if (sortBy === 'person_name_asc') {
			const aN = `${a.person_name || ''} ${a.person_surname || ''}`.trim();
			const bN = `${b.person_name || ''} ${b.person_surname || ''}`.trim();
			return aN.localeCompare(bN);
		}
		if (sortBy === 'person_name_desc') {
			const aN = `${a.person_name || ''} ${a.person_surname || ''}`.trim();
			const bN = `${b.person_name || ''} ${b.person_surname || ''}`.trim();
			return bN.localeCompare(aN);
		}
		return 0;
	});

	if (loading) {
		return (
			<div className={`flex flex-col items-center justify-center h-64 font-mono text-xs ${isDark ? 'text-emerald-500' : 'text-emerald-600'}`}>
				<div className="flex items-center space-x-2">
					<div className="w-1 h-3 bg-current animate-pulse" />
					<span>[ FETCHING_SOCIAL_RECORDS... ]</span>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-4 px-2 md:px-0 pb-20">
			{/* Header */}
			<div className={`flex flex-col md:flex-row items-start justify-between md:items-center p-4 border rounded-none ${isDark ? 'bg-[#111] border-[#222]' : 'bg-white border-neutral-200'} gap-4`}>
				<div>
					<h1 className={`text-sm font-mono font-bold tracking-widest uppercase ${isDark ? 'text-neutral-200' : 'text-neutral-800'}`}>
						// SYS: SOCIAL_INTEL
					</h1>
					<p className={`text-[10px] font-mono mt-0.5 uppercase tracking-wider ${isDark ? 'text-emerald-500/70' : 'text-emerald-600/70'}`}>
						{globalSearchTerm ? `MATCHING_RECORDS: ${filteredSocials.length} / ${socials.length}` : `TOTAL_RECORDS: ${filteredSocials.length}`}
					</p>
				</div>
				<div className="flex items-center space-x-2 self-end md:self-auto flex-wrap gap-2">
					<select
						value={sortBy}
						onChange={(e) => setSortBy(e.target.value)}
						className={`px-3 py-1.5 text-[10px] font-mono uppercase tracking-wider border outline-none transition-colors ${isDark ? 'bg-[#0a0a0a] border-[#333] text-neutral-400 focus:border-emerald-500/50' : 'bg-neutral-50 border-neutral-300 text-neutral-600 focus:border-emerald-500/50'}`}
					>
						<option value="create_time_desc">NEWEST_FIRST</option>
						<option value="create_time_asc">OLDEST_FIRST</option>
						<option value="username_asc">USERNAME_ASC</option>
						<option value="username_desc">USERNAME_DESC</option>
						<option value="person_name_asc">ENTITY_NAME_ASC</option>
						<option value="person_name_desc">ENTITY_NAME_DESC</option>
					</select>
					<button
						onClick={() => setShowHidden(!showHidden)}
						className={`flex items-center space-x-1.5 px-3 py-1.5 text-[10px] font-mono tracking-wider uppercase border transition-colors ${
							isDark 
								? showHidden ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : 'bg-[#0a0a0a] border-[#333] text-neutral-500 hover:text-white' 
								: showHidden ? 'bg-emerald-50 border-emerald-200 text-emerald-600' : 'bg-neutral-50 border-neutral-300 text-neutral-600 hover:text-black'
						}`}
						title={showHidden ? 'HIDE_SECURE_RECORDS' : 'SHOW_SECURE_RECORDS'}
					>
						{showHidden ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
						<span className="hidden sm:inline">{showHidden ? 'SECURE_VISIBLE' : 'SECURE_HIDDEN'}</span>
					</button>
				</div>
			</div>

			{sortedSocials.length > 0 ? (
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
					{sortedSocials.map((social) => {
						const username = getUsername(social);
						const name = getName(social);
						const profilePicUrl = getProfilePicUrl(social);
						const socialId = social.id || social.iu_id || social.vu_id || social.tu_id || social.xu_id || social.su_id;
						const isInstagram = social.platform === 'instagram';
						const isTiktok = social.platform === 'tiktok';
						const isVsco = social.platform === 'vsco';
						const platformMapVal = PLATFORM_MAP[social.platform] || social.platform;

						return (
							<div
								key={`${social.platform}-${socialId}`}
								onClick={() => navigate(`/stalker/socials/${social.platform}/${socialId}`)}
								className={`p-4 border relative group transition-colors cursor-pointer flex flex-col justify-between h-full ${isDark ? 'bg-[#111] hover:bg-[#1a1a1a] border-[#222] hover:border-[#444]' : 'bg-white hover:bg-neutral-50 border-neutral-200'}`}
							>
								<div>
									{/* Person Info */}
									<div className={`flex items-center justify-between mb-3 pb-3 border-b ${isDark ? 'border-[#333]' : 'border-neutral-200'}`}>
										<div className="flex items-center space-x-2">
											{social.person_avatar ? (
												<img
													src={getStaticFileUrl(social.person_avatar) || ''}
													alt="AVT"
													className={`w-6 h-6 object-cover border ${isDark ? 'border-[#444]' : 'border-neutral-300'}`}
												/>
											) : (
												<div className={`w-6 h-6 flex items-center justify-center font-mono text-[8px] border rounded-none ${isDark ? 'bg-[#1a1a1a] text-neutral-500 border-[#333]' : 'bg-neutral-100 text-neutral-500 border-neutral-300'}`}>
													{(social.person_name || '').charAt(0)}{(social.person_surname || '').charAt(0)}
												</div>
											)}
											<div className="flex flex-col">
												<span className={`text-[11px] font-mono font-bold tracking-wider uppercase truncate max-w-[120px] ${isDark ? 'text-neutral-300' : 'text-neutral-800'}`}>
													{social.person_name} {social.person_surname}
												</span>
												<span className={`text-[9px] font-mono tracking-widest uppercase ${isDark ? 'text-emerald-500/60' : 'text-emerald-600/60'}`}>
													// {platformMapVal}
												</span>
											</div>
										</div>
										<span className={`text-[9px] font-mono px-1.5 py-0.5 border ${isDark ? 'border-[#333] text-neutral-500' : 'border-neutral-200 text-neutral-400'}`}>
											ID:{socialId}
										</span>
									</div>

									{/* Social Info */}
									<div className="flex items-start space-x-3 mb-4">
										<div className={`w-12 h-12 flex-shrink-0 border p-0.5 ${isDark ? 'border-[#333] bg-[#0a0a0a]' : 'border-neutral-200 bg-white'}`}>
											{profilePicUrl ? (
												<img 
													src={isTiktok || isVsco ? (() => {
														const url = getProxyUrl(profilePicUrl) || profilePicUrl || '';
														return url && !url.startsWith('http') ? `https://${url}` : url;
													})() : getProxyUrl(profilePicUrl) || ''} 
													alt={username}
													className="w-full h-full object-cover"
												/>
											) : (
												<div className={`w-full h-full flex items-center justify-center ${isDark ? 'bg-[#111]' : 'bg-neutral-100'}`}>
													{isTiktok ? (
														<Music className={`w-5 h-5 ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`} />
													) : isVsco ? (
														<ImageIcon className={`w-5 h-5 ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`} />
													) : (
														<Instagram className={`w-5 h-5 ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`} />
													)}
												</div>
											)}
										</div>
										<div className="flex-1 min-w-0 font-mono text-[10px] uppercase pt-1">
											<div className="flex items-center space-x-1.5 overflow-hidden">
												<span className={`font-bold truncate tracking-wider text-[12px] ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
													@{username}
												</span>
												{getIsVerified(social) && (
													<CheckCircle className="w-3 h-3 text-blue-500 flex-shrink-0" strokeWidth={2.5} />
												)}
												{getIsPrivate(social) && (
													<Lock className={`w-3 h-3 flex-shrink-0 ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`} />
												)}
											</div>
											{name && (
												<div className={`truncate mt-1 tracking-wider text-[11px] ${isDark ? 'text-neutral-400' : 'text-neutral-500'}`}>{name}</div>
											)}
										</div>
									</div>
								</div>
                                
                                {/* Stats grid */}
								{!(isVsco || (!isInstagram && !isTiktok)) && (
									<div className={`mt-auto grid grid-cols-3 gap-2 p-2 border ${isDark ? 'bg-[#0a0a0a] border-[#222]' : 'bg-neutral-50 border-neutral-200'}`}>
										<div className="flex flex-col space-y-1">
											{histories[`${social.platform}-${socialId}`]?.followers ? (
												<div className="h-6">
													<MiniLineChart 
														points={histories[`${social.platform}-${socialId}`].followers} 
														color={isDark ? '#10b981' : '#059669'} 
														defaultLabelValue={getFollowerCount(social)} 
														defaultLabelDate={social.created_at} 
													/>
												</div>
											) : (
												<div className="h-6"><LoadingMiniLineChart isDark={isDark} /></div>
											)}
											<span className={`text-center font-bold tracking-wider font-mono text-[10px] ${isDark ? 'text-neutral-100' : 'text-neutral-800'}`}>
												{getFollowerCount(social)?.toLocaleString() || '0'}
											</span>
											<span className={`text-center text-[8px] font-mono tracking-widest ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>F_LWR</span>
										</div>
										<div className="flex flex-col space-y-1">
											{histories[`${social.platform}-${socialId}`]?.following ? (
												<div className="h-6">
													<MiniLineChart 
														points={histories[`${social.platform}-${socialId}`].following} 
														color={isDark ? '#3b82f6' : '#2563eb'} 
														defaultLabelValue={getFollowingCount(social)} 
														defaultLabelDate={social.created_at} 
													/>
												</div>
											) : (
												<div className="h-6"><LoadingMiniLineChart isDark={isDark} /></div>
											)}
											<span className={`text-center font-bold tracking-wider font-mono text-[10px] ${isDark ? 'text-neutral-100' : 'text-neutral-800'}`}>
												{getFollowingCount(social)?.toLocaleString() || '0'}
											</span>
											<span className={`text-center text-[8px] font-mono tracking-widest ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>F_ING</span>
										</div>
										<div className="flex flex-col space-y-1">
											{histories[`${social.platform}-${socialId}`]?.media ? (
												<div className="h-6">
													<MiniLineChart 
														points={histories[`${social.platform}-${socialId}`].media} 
														color={isDark ? '#eab308' : '#ca8a04'} 
														defaultLabelValue={getMediaCount(social)} 
														defaultLabelDate={social.created_at} 
													/>
												</div>
											) : (
												<div className="h-6"><LoadingMiniLineChart isDark={isDark} /></div>
											)}
											<span className={`text-center font-bold tracking-wider font-mono text-[10px] ${isDark ? 'text-neutral-100' : 'text-neutral-800'}`}>
												{getMediaCount(social)?.toLocaleString() || '0'}
											</span>
											<span className={`text-center text-[8px] font-mono tracking-widest ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>MEDIA</span>
										</div>
									</div>
								)}

								{getSocialUrl(social) && (
									<div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
										<a
											href={getSocialUrl(social) || ''}
											target="_blank"
											rel="noopener noreferrer"
											onClick={(e) => e.stopPropagation()}
											className={`flex items-center justify-center p-1.5 border transition-all ${isDark ? 'bg-[#0a0a0a] border-[#333] hover:border-emerald-500/50 hover:text-emerald-400 text-neutral-500' : 'bg-white border-neutral-300 hover:border-emerald-500/50 hover:text-emerald-600 text-neutral-400'}`}
											title={`EXTERNAL_LINK // ${platformMapVal}`}
										>
											<ExternalLink className="w-3.5 h-3.5" />
										</a>
									</div>
								)}
							</div>
						);
					})}
				</div>
			) : (
				<div className={`border p-8 text-center font-mono text-xs uppercase tracking-widest ${isDark ? 'bg-[#111] border-[#222] text-neutral-600' : 'bg-white border-neutral-200 text-neutral-400'}`}>
					[ NO_RECORDS_FOUND ]
				</div>
			)}
		</div>
	);
};

export default Socials;

